import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import CustomerLayout from "@/components/CustomerLayout";
import MasterProductCard from "@/components/MasterProductCard";
import { SkeletonCard, EmptyState } from "@/components/States";
import { PackageSearch, Search } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { useLocation } from "@/context/LocationContext";

const sel = "rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand";

export default function CategoryPage() {
  const { slug } = useParams();
  const { location } = useLocation();
  const [sub, setSub] = useState("");
  const [q, setQ] = useState("");
  const [brand, setBrand] = useState("");
  const [sort, setSort] = useState("featured");
  const [available, setAvailable] = useState("");
  const [page, setPage] = useState(1);
  const label = slug.replace(/-/g, " ");
  useSeo({ title: `${label.replace(/\b\w/g, (c) => c.toUpperCase())} — Order online`, description: `Shop ${label} from local stores near you on Grofkart.`, path: `/category/${slug}` });
  const params = { subcategory: sub, q, brand, sort, available, page, lat: location?.lat, lng: location?.lng };
  const { data, isLoading } = useQuery({ queryKey: ["cat-master", slug, params], queryFn: () => api.get(`/catalog/category/${slug}`, { params }).then((r) => r.data), keepPreviousData: true });
  const pages = data ? Math.max(1, Math.ceil(data.total / 24)) : 1;

  return (
    <CustomerLayout>
      <h1 className="mb-1 font-head text-2xl font-extrabold text-slate-900 sm:text-3xl" data-testid="category-title">{data?.category?.name || label}</h1>
      <p className="mb-4 text-sm text-slate-500">{data ? `${data.total.toLocaleString("en-IN")} products in the Grofkart catalog` : "Loading…"}</p>

      {data?.subcategories?.length > 0 && (
        <div className="mb-4 flex gap-2 overflow-x-auto pb-1" data-testid="subcategory-chips">
          <button onClick={() => { setSub(""); setPage(1); }} className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-bold ${!sub ? "bg-brand text-white" : "border border-slate-200 bg-white text-slate-600"}`}>All</button>
          {data.subcategories.map((s) => <button key={s.slug} onClick={() => { setSub(s.slug); setPage(1); }} data-testid={`subcat-${s.slug}`} className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-bold ${sub === s.slug ? "bg-brand text-white" : "border border-slate-200 bg-white text-slate-600"}`}>{s.name}</button>)}
        </div>
      )}

      <div className="mb-5 flex flex-wrap gap-2">
        <div className={`flex flex-1 min-w-[180px] items-center gap-2 ${sel}`}><Search className="h-4 w-4 text-slate-400" /><input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search in this category" data-testid="category-search" className="w-full bg-transparent outline-none" /></div>
        <select value={brand} onChange={(e) => { setBrand(e.target.value); setPage(1); }} data-testid="category-brand" className={sel}><option value="">All brands</option>{(data?.brands || []).map((b) => <option key={b}>{b}</option>)}</select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="category-sort" className={sel}><option value="featured">Featured</option><option value="new">Newest</option><option value="mrp_asc">MRP: low to high</option><option value="mrp_desc">MRP: high to low</option><option value="name">Name A–Z</option></select>
        <select value={available} onChange={(e) => setAvailable(e.target.value)} data-testid="category-available" className={sel}><option value="">All products</option><option value="yes">Available near me</option></select>
      </div>

      {isLoading && !data ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">{Array.from({ length: 10 }).map((_, i) => <SkeletonCard key={i} />)}</div>
      ) : data?.items?.length ? (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5" data-testid="category-grid">
            {data.items.map((p) => <MasterProductCard key={p.id} item={p} />)}
          </div>
          {pages > 1 && (
            <div className="mt-6 flex items-center justify-center gap-3 text-sm">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded-lg border px-3 py-1.5 disabled:opacity-40">Prev</button>
              <span>Page {page} / {pages}</span>
              <button disabled={page >= pages} onClick={() => setPage(page + 1)} className="rounded-lg border px-3 py-1.5 disabled:opacity-40">Next</button>
            </div>
          )}
        </>
      ) : (
        <EmptyState icon={PackageSearch} title="No products in this category yet" subtitle="The Grofkart catalog is growing — check back soon." />
      )}
    </CustomerLayout>
  );
}
